import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || '';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const MaintenanceContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #1e1b4b 70%, #312e81 100%);
  padding: 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 600px 400px at 20% 50%, rgba(99,102,241,.15) 0%, transparent 70%),
      radial-gradient(ellipse 500px 350px at 80% 30%, rgba(239,68,68,.08) 0%, transparent 70%),
      radial-gradient(ellipse 300px 300px at 50% 90%, rgba(6,182,212,.1) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const MaintenanceCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border-radius: 24px;
  padding: 3rem 2.5rem;
  max-width: 580px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.05);
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
  z-index: 1;

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    border-radius: 18px;
  }
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  animation: ${float} 3s ease-in-out infinite;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  margin-bottom: 0.75rem;
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 1.4rem;
  }
`;

const Message = styled.p`
  color: #475569;
  font-size: 1.05rem;
  line-height: 1.7;
  margin-bottom: 1.25rem;

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const CompanyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  border: 1px solid #cbd5e1;
  border-radius: 100px;
  padding: 0.5rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-top: 1rem;
`;

const DeadlineChip = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #fef2f2, #fee2e2);
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 0.6rem 1.25rem;
  font-size: 0.9rem;
  color: #991b1b;
  font-weight: 600;
  margin-bottom: 1.25rem;
`;

const ShimmerBar = styled.div`
  height: 3px;
  border-radius: 2px;
  margin: 1.5rem auto 0;
  width: 120px;
  background: linear-gradient(90deg, #e2e8f0, #6366f1, #e2e8f0);
  background-size: 200% 100%;
  animation: ${shimmer} 2s linear infinite;
`;

const MaintenancePage: React.FC = () => {
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [applicationDeadline, setApplicationDeadline] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/maintenance-status`);
        const data = await res.json();
        if (data.deadlinePassed) setDeadlinePassed(true);
        if (data.applicationDeadline) setApplicationDeadline(data.applicationDeadline);
      } catch {}
    };
    fetchStatus();
  }, []);

  // Deadline-passed variant: Application acceptance closed
  if (deadlinePassed) {
    return (
      <MaintenanceContainer>
        <MaintenanceCard>
          <IconContainer style={{ background: 'linear-gradient(135deg, #fef2f2, #fee2e2)' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </IconContainer>
          <Title style={{ color: '#991b1b' }}>Applications Closed</Title>
          {applicationDeadline && (
            <DeadlineChip>
              Deadline: {new Date(applicationDeadline).toLocaleString('en-IN', {
                day: 'numeric', month: 'long', year: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: true
              })}
            </DeadlineChip>
          )}
          <Message>
            <strong>Maharashtra Natural Gas Limited (MNGL)</strong> has stopped accepting applications
            for the <strong>CNG Dealership</strong>.
          </Message>
          <Message style={{ color: '#64748b', fontSize: '0.95rem' }}>
            The application window has closed. No new applications are being accepted at this time.
            Thank you for your interest.
          </Message>
          <CompanyBadge>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            MNGL DODO CNG Dealership Portal
          </CompanyBadge>
          <ShimmerBar />
        </MaintenanceCard>
      </MaintenanceContainer>
    );
  }

  // Default maintenance variant
  return (
    <MaintenanceContainer>
      <MaintenanceCard>
        <IconContainer style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </IconContainer>
        <Title style={{ color: '#1e293b' }}>Under Maintenance</Title>
        <Message>
          We're currently performing scheduled maintenance to improve our services.
        </Message>
        <Message style={{ color: '#64748b', fontSize: '0.95rem' }}>
          Please check back later. We apologize for any inconvenience and appreciate your patience.
        </Message>
        <CompanyBadge>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          MNGL DODO CNG Dealership Portal
        </CompanyBadge>
        <ShimmerBar />
      </MaintenanceCard>
    </MaintenanceContainer>
  );
};

export default MaintenancePage;
